/**
 * Hybrid hierarchical+searchable picker popover (NGTPA-24052).
 *
 * Two render modes driven by the search input:
 *   - Browse (no query): collapsed tree of expandable groups with leaves at the
 *     bottom — shape-of-the-data discoverability.
 *   - Find (non-empty query): flat list of matching leaves, each with a
 *     breadcrumb of its parent groups — every match visible without an extra
 *     click. Mirrors the inline @mention picker UX (`suggestion.js`).
 *
 * The shared shell owns popover + search state + filtering. Callers own the
 * trigger and pass `open`/`anchorEl`/`onSelect`. Used by both the template
 * token toolbar button and the rules-engine QueryBuilder field selector.
 */

import React, { useMemo, useState } from 'react';

import MenuItem from '~/components/core/Atomic/MenuItem';
import { sortSuggestionOptions } from '~/components/core/Editor/MenuBar/suggestion/suggestion';
import SuggestionMenuItem from '~/components/core/Editor/MenuBar/suggestion/SuggestionMenuItem';
import type { SelectedToken, SuggestionOption } from '~/components/core/Editor/types';
import TextField from '~/components/core/Molecules/Fields/TextField';
import SearchIcon from '~/components/icons/SearchIcon';
import Divider from '~/components/ui/atoms/Divider';
import Popover from '~/components/ui/atoms/Popover';

// Match-quality tiers, ordered from strongest (1) to weakest (5). Direct
// label matches (1–3) precede path-only matches (4–5) — without this,
// typing "loss" surfaces a tree-order result before "Loss Location" because
// some earlier-in-tree field happened to live under a group whose path
// contained "loss".
enum MatchTier {
  ExactLabel = 1,
  LabelPrefix = 2,
  LabelSubstring = 3,
  PathSegmentPrefix = 4,
  PathSubstring = 5,
}

const DIRECT_MATCH_MAX_TIER = MatchTier.LabelSubstring;
// Above this count of indirect (path-only) matches, hide them behind a
// "More from related groups" expander so a query like "claim" doesn't
// dump 50 path-only hits into the visible list.
const INDIRECT_MATCH_COLLAPSE_THRESHOLD = 5;

type FlatMatch = {
  id: string;
  label: string;
  groupPath: string[];
  tags?: string[];
  tier: MatchTier;
};

const classifyMatch = (label: string, path: string[], q: string): MatchTier | null => {
  const lowerLabel = label.toLowerCase();
  if (lowerLabel === q) return MatchTier.ExactLabel;
  if (lowerLabel.startsWith(q)) return MatchTier.LabelPrefix;
  if (lowerLabel.includes(q)) return MatchTier.LabelSubstring;
  // Path tiers check the parent segments (the label is already covered above).
  // Prefix-on-any-segment beats mid-string-in-the-joined-path: typing "claim"
  // ranks `Claim › Incident › Date Of Loss` (segment startsWith) above
  // `Some Other Group › 2024 claim audit` (substring-only).
  // Both tiers iterate segments individually rather than joining — joining
  // with a space (or any separator) would let queries like "m i" match across
  // segment boundaries (`Clai m › I ncident`) without the display being able
  // to highlight them, since the breadcrumb renders as `Claim › Incident`.
  // Per-segment matching keeps the search behavior and the bolded display
  // in lockstep.
  const parentSegments = path.slice(0, -1);
  for (const segment of parentSegments) {
    if (segment.toLowerCase().startsWith(q)) return MatchTier.PathSegmentPrefix;
  }
  if (parentSegments.some((segment) => segment.toLowerCase().includes(q))) return MatchTier.PathSubstring;
  return null;
};

/**
 * Walk the tree and return matching leaves with their parent path. A leaf
 * matches when its label OR its full path (joined) contains the query
 * (case-insensitive). Groups are not returned as standalone results — if a
 * group name matches, only its descendant leaves surface (each carries the
 * matching segment in its breadcrumb).
 *
 * Each match also carries a `tier` (see `MatchTier`) reflecting how strongly
 * the query matched. Callers sort by tier first, then alphabetically by
 * label, so the strongest match always appears at the top regardless of where
 * it sits in the source tree.
 *
 * `tags` are forwarded so search-mode results render the same `(Person)` /
 * `(Property)` annotations the browse-mode `SuggestionMenuItem` shows.
 */
const flattenMatches = (
  tree: ReadonlyArray<SuggestionOption>,
  query: string,
  parentPath: string[] = []
): FlatMatch[] => {
  const q = query.toLowerCase();
  const out: FlatMatch[] = [];
  for (const item of tree) {
    const path = [...parentPath, item.label];
    if (item.sub_items && item.sub_items.length > 0) {
      out.push(...flattenMatches(item.sub_items, query, path));
      continue;
    }
    const tier = classifyMatch(item.label, path, q);
    if (tier !== null) {
      out.push({ id: item.id, label: item.label, groupPath: parentPath, tags: item.tags, tier });
    }
  }
  return out;
};

const sortMatchesByRank = (matches: FlatMatch[]): FlatMatch[] =>
  [...matches].sort((a, b) => a.tier - b.tier || a.label.localeCompare(b.label));

// Split `text` into segments around case-insensitive occurrences of `query`.
// Used to bold the matched portion of a label so a ranked list is easy to
// scan visually.
const splitForHighlight = (text: string, query: string): Array<{ value: string; matched: boolean }> => {
  if (!query) return [{ value: text, matched: false }];
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const out: Array<{ value: string; matched: boolean }> = [];
  let cursor = 0;
  while (cursor < text.length) {
    const hit = lower.indexOf(q, cursor);
    if (hit === -1) {
      out.push({ value: text.slice(cursor), matched: false });
      break;
    }
    if (hit > cursor) {
      out.push({ value: text.slice(cursor, hit), matched: false });
    }
    out.push({ value: text.slice(hit, hit + q.length), matched: true });
    cursor = hit + q.length;
  }
  return out;
};

const HighlightedText: React.FC<{ text: string; query: string }> = ({ text, query }) => (
  <>
    {splitForHighlight(text, query).map((segment, idx) =>
      segment.matched ? <strong key={idx}>{segment.value}</strong> : <span key={idx}>{segment.value}</span>
    )}
  </>
);

interface HierarchicalSearchablePopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  options: ReadonlyArray<SuggestionOption>;
  /** Leaves pinned above the tree, in the order given. `sortSuggestionOptions`
   *  puts directories first, so an entry that must lead the list cannot get there
   *  by being a root-level option — it has to bypass the sort. Used by the rules
   *  engine's anchor picker for its two non-field modes. Still searchable: they
   *  join the ranked matches like any other leaf. */
  leadingOptions?: ReadonlyArray<SuggestionOption>;
  onClose: () => void;
  onSelect: (token: SelectedToken) => void;
  /** Names the popover. */
  label: string;
  /** The popover's id, which the trigger's `aria-controls` names. `usePopoverState` supplies both. */
  id?: string;
  /** data-testid for the search input — used by call-site tests. */
  searchTestId?: string;
  /** The option whose value the trigger is currently showing, for a menu that picks a *value*.
   *  Marks that row selected and opens the groups above it so it is on screen when the menu
   *  opens. Omitted by the two token pickers, which insert rather than pick and so have no
   *  current value — without it every row renders exactly as before. */
  selectedId?: string;
  /** Rendered after a leaf's label, in the tree and in search results alike — the rules-engine
   *  field picker marks its collection fields this way. Leaves only: see `SuggestionMenuItem`.
   *  Omitted by the two token pickers, whose rows render exactly as before. */
  renderLeafAdornment?: (id: string) => React.ReactNode;
}

const HierarchicalSearchablePopover: React.FC<HierarchicalSearchablePopoverProps> = ({
  open,
  anchorEl,
  options,
  leadingOptions,
  onClose,
  onSelect,
  label,
  id,
  searchTestId,
  selectedId,
  renderLeafAdornment,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllIndirect, setShowAllIndirect] = useState(false);
  const trimmedQuery = searchQuery.trim();
  const isSearching = trimmedQuery.length > 0;

  const sortedTree = useMemo(() => sortSuggestionOptions(options), [options]);
  // Leading entries keep their pinned order in search too: they are the modes the
  // control offers, not results to be ranked among the fields.
  const leadingMatches = useMemo(
    () => (isSearching ? flattenMatches(leadingOptions ?? [], trimmedQuery) : []),
    [leadingOptions, isSearching, trimmedQuery]
  );
  const rankedMatches = useMemo(
    () => (isSearching ? sortMatchesByRank(flattenMatches(options, trimmedQuery)) : []),
    [options, isSearching, trimmedQuery]
  );
  // Direct = the label itself matched (any of the first three tiers). Indirect
  // = a parent group's name matched but the leaf's own label didn't — these
  // are still useful, but burying them under an expander prevents a query like
  // "claim" from drowning out direct hits with 50 path-only matches.
  const directMatches = useMemo(() => rankedMatches.filter((m) => m.tier <= DIRECT_MATCH_MAX_TIER), [rankedMatches]);
  const indirectMatches = useMemo(() => rankedMatches.filter((m) => m.tier > DIRECT_MATCH_MAX_TIER), [rankedMatches]);
  // Only collapse when there's something else to look at — if direct matches
  // are empty, hiding indirect ones leaves a "No results" feeling.
  const shouldCollapseIndirect =
    directMatches.length > 0 && indirectMatches.length >= INDIRECT_MATCH_COLLAPSE_THRESHOLD;
  const visibleIndirect = shouldCollapseIndirect && !showAllIndirect ? [] : indirectMatches;

  const handleClose = () => {
    setSearchQuery('');
    setShowAllIndirect(false);
    onClose();
  };

  const handleSelect = (token: SelectedToken) => {
    setSearchQuery('');
    setShowAllIndirect(false);
    onSelect(token);
  };

  // Reset the "show all" expander whenever the query changes — a fresh search
  // shouldn't inherit the previous query's expansion state.
  React.useEffect(() => {
    setShowAllIndirect(false);
  }, [trimmedQuery]);

  // Shared row renderer for both direct and indirect match lists. Inlined
  // (not extracted to a separate component) because it closes over
  // `trimmedQuery` and `handleSelect`; a standalone component would need
  // them passed as props with no rendering benefit.
  const renderMatch = ({ id, label, groupPath, tags }: FlatMatch) => (
    <MenuItem
      key={id}
      // Marked in search results as well as in the tree: a query that still matches the current
      // value should not make the menu forget which one it is.
      selected={selectedId !== undefined && id === selectedId}
      onClick={() => handleSelect({ id, label, groupPath: groupPath.length > 0 ? groupPath : undefined })}
      className="pl-12"
    >
      <div>
        <div>
          <HighlightedText text={label} query={trimmedQuery} />
          {tags?.map((tag) => (
            <span key={tag} className="ml-4 text-xs text-slate-700">
              ({tag})
            </span>
          ))}
          {renderLeafAdornment?.(id)}
        </div>
        {groupPath.length > 0 && (
          <div className="text-xs text-slate-700">
            {/* Render each segment with its own `HighlightedText` and a plain
                chevron between them. Highlighting the joined string would
                over-bold: every occurrence of the query in any segment would
                light up (including ones that didn't earn the leaf's match
                tier). Per-segment highlighting matches the per-segment search
                semantics — each bolded chunk reflects a real segment match. */}
            {groupPath.map((segment, i) => (
              <React.Fragment key={i}>
                {i > 0 && ' › '}
                <HighlightedText text={segment} query={trimmedQuery} />
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </MenuItem>
  );

  return (
    <Popover open={open} anchorEl={anchorEl} onClose={handleClose} id={id} aria-label={label} align="start">
      <div className="w-[300px]">
        <div className="px-12 py-8">
          <TextField
            className="pb-12"
            value={searchQuery}
            onChange={(value: string) => setSearchQuery(value)}
            label="Search"
            fullWidth
            autoFocus
            inputProps={searchTestId ? { 'data-testid': searchTestId } : undefined}
            InputProps={{
              endAdornment: (
                <span className="flex items-center">
                  <SearchIcon className="text-slate-400" size={16} />
                </span>
              ),
            }}
          />
        </div>
        <Divider />
        <div className="max-h-[350px] overflow-y-auto">
          {isSearching ? (
            leadingMatches.length + rankedMatches.length > 0 ? (
              <>
                {leadingMatches.map(renderMatch)}
                {directMatches.map(renderMatch)}
                {shouldCollapseIndirect && !showAllIndirect && directMatches.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllIndirect(true)}
                    className="block w-full cursor-pointer border-0 bg-transparent px-12 py-8 text-left text-xs text-slate-600 hover:bg-slate-100"
                  >
                    More from related groups ({indirectMatches.length})
                  </button>
                )}
                {visibleIndirect.map(renderMatch)}
              </>
            ) : (
              <div className="px-12 py-8 text-sm text-slate-500">No results found</div>
            )
          ) : leadingOptions?.length || sortedTree.length > 0 ? (
            <>
              {leadingOptions?.map(({ id, label, sub_items }) => (
                <SuggestionMenuItem
                  key={id}
                  id={id}
                  label={label}
                  subItems={sub_items}
                  handleSelectItem={handleSelect}
                  selectedId={selectedId}
                  renderLeafAdornment={renderLeafAdornment}
                />
              ))}
              {leadingOptions?.length ? <Divider /> : null}
              {sortedTree.map(({ id, label, sub_items }) => (
                <SuggestionMenuItem
                  key={id}
                  id={id}
                  label={label}
                  subItems={sub_items}
                  handleSelectItem={handleSelect}
                  selectedId={selectedId}
                  renderLeafAdornment={renderLeafAdornment}
                />
              ))}
            </>
          ) : (
            <div className="px-12 py-8 text-sm text-slate-500">No results found</div>
          )}
        </div>
      </div>
    </Popover>
  );
};

export default HierarchicalSearchablePopover;
